/** Formatting + row definitions for the Daily DOR, shared by the KPI cards and both sheets. */

export type Kind = 'int' | 'num' | 'km' | 'pct' | 'ratio' | 'dur' | 'avgdur' | 'kwh'

export interface DorRow {
  label: string
  key: string            // dotted path into a DorDay / DorMonth
  kind: Kind
  month?: boolean        // also has prev/cur month values
  target?: boolean       // a target line: shown muted
  strong?: boolean       // the headline line of its section
  tone?: boolean         // colour the value against 100% (pct rows)
  source?: string
}

export interface DorSection {
  id: string
  title: string
  accent: string         // tailwind bg-* for the section marker
  rows: DorRow[]
}

const SRC_KM = 'Telemetry odometer, summed per IST day'
const SRC_TIMS = 'TIMS Dhar gate-out (DHAR_OUT) scans, one per LR'
const SRC_TAT = 'GPS geofence visits at Dhar yard / plant, departing that day'
const SRC_BD = 'Breakdown log — incidents open at any point that day'

export function dorSections(vendors: string[]): DorSection[] {
  return [
    {
      id: 'fleet', title: 'Fleet & kilometres', accent: 'bg-sky-500', rows: [
        { label: 'Vehicles', key: 'vehicles', kind: 'num', month: true, source: 'Ultratech vehicles reporting telemetry in the month' },
        { label: 'Active vehicles', key: 'active', kind: 'num', month: true, source: 'Ran more than 20 km that day' },
        { label: 'Kms (actual run)', key: 'kms', kind: 'km', month: true, strong: true, source: SRC_KM },
        { label: 'Target km / vehicle', key: 'target_km_per_veh', kind: 'km', month: true, target: true },
        { label: 'Km / active vehicle', key: 'km_per_active', kind: 'km', month: true },
        { label: 'Utilisation', key: 'utilization', kind: 'pct', month: true, strong: true, tone: true },
      ],
    },
    {
      id: 'trips', title: 'Trips', accent: 'bg-indigo-500', rows: [
        { label: 'Target trips (fleet)', key: 'target_trips', kind: 'num', month: true, target: true },
        { label: 'Target trips (active)', key: 'target_trips_active', kind: 'num', month: true, target: true },
        { label: 'Trips completed — TIMS', key: 'trips', kind: 'int', month: true, strong: true, source: SRC_TIMS },
        { label: 'Trips completed — indicative', key: 'trips_indicative', kind: 'num', month: true, source: 'Kms ÷ target km per vehicle' },
        { label: 'Balance pending — indicative', key: 'balance_indicative', kind: 'num', month: true },
        { label: 'Trips / vehicle', key: 'trips_per_veh', kind: 'ratio', month: true },
        { label: 'Trips / active vehicle', key: 'trips_per_active', kind: 'ratio', month: true },
      ],
    },
    {
      id: 'tonnage', title: 'Tonnage', accent: 'bg-violet-500', rows: [
        { label: 'Target tonnage', key: 'target_tonnage', kind: 'km', month: true, target: true },
        { label: 'Tonnage delivered — TIMS', key: 'tonnage', kind: 'num', month: true, strong: true, source: 'TIMS DHAR_OUT transaction tonnage (5–60 t per LR)' },
        { label: 'Target achieved', key: 'tonnage_pct', kind: 'pct', month: true, tone: true },
      ],
    },
    {
      id: 'loading', title: 'Loading TAT at Dhar', accent: 'bg-amber-500', rows: [
        { label: 'Loading count (vehicles)', key: 'loading.count', kind: 'int', source: SRC_TAT },
        { label: 'Yard — total', key: 'loading.yard_sec', kind: 'dur', source: SRC_TAT },
        { label: 'Plant — total', key: 'loading.plant_sec', kind: 'dur', source: SRC_TAT },
        { label: 'Total loading TAT', key: 'loading.total_sec', kind: 'dur' },
        { label: 'Yard — avg / vehicle', key: 'loading.avg_yard_sec', kind: 'avgdur' },
        { label: 'Plant — avg / vehicle', key: 'loading.avg_plant_sec', kind: 'avgdur' },
        { label: 'Average loading TAT', key: 'loading.avg_total_sec', kind: 'avgdur', strong: true },
      ],
    },
    {
      id: 'cumulative', title: 'Cumulative (from 1st of previous month)', accent: 'bg-teal-500', rows: [
        { label: 'Trips — target', key: 'cum_trips.target', kind: 'km', target: true },
        { label: 'Trips — completed', key: 'cum_trips.completed', kind: 'km' },
        { label: 'Trips — balance', key: 'cum_trips.balance', kind: 'km' },
        { label: 'Trips — efficiency', key: 'cum_trips.efficiency', kind: 'pct', strong: true, tone: true },
        { label: 'Tonnage — target', key: 'cum_tonnage.target', kind: 'km', target: true },
        { label: 'Tonnage — delivered', key: 'cum_tonnage.delivered', kind: 'km' },
        { label: 'Tonnage — balance', key: 'cum_tonnage.balance', kind: 'km' },
        { label: 'Tonnage — efficiency', key: 'cum_tonnage.efficiency', kind: 'pct', strong: true, tone: true },
      ],
    },
    {
      id: 'breakdowns', title: 'Truck breakdowns', accent: 'bg-rose-500', rows: [
        { label: 'Accident', key: 'truck.accident', kind: 'int', source: SRC_BD },
        { label: 'Maintenance', key: 'truck.maintenance', kind: 'int', source: SRC_BD },
        { label: 'Total', key: 'truck.total', kind: 'int', strong: true },
      ],
    },
    {
      id: 'drivers', title: 'Drivers present (dispatch)', accent: 'bg-emerald-500', rows: [
        ...vendors.map((v): DorRow => ({ label: v, key: `drivers.${v}`, kind: 'int', source: 'Distinct TIMS drivers on the day’s dispatches, by TIMS vendor' })),
        { label: 'Total present', key: 'drivers_total', kind: 'int', strong: true },
      ],
    },
    {
      id: 'energy', title: 'Energy — charging (kWh)', accent: 'bg-lime-500', rows: [
        { label: 'Dhar', key: 'energy.DHAR', kind: 'kwh', source: 'Charging energy from telemetry; nested fences merged' },
        { label: 'Julwaniya', key: 'energy.JHUL', kind: 'kwh' },
        { label: 'Dhule', key: 'energy.DHULE', kind: 'kwh' },
        { label: 'Total', key: 'energy_total', kind: 'kwh', strong: true },
        { label: 'kWh / km', key: 'kwh_per_km', kind: 'ratio' },
      ],
    },
  ]
}

/** Workbook rows the platform has no source for -- blank in the Excel download too. */
export const UNTRACKED_ROWS = ['Reason for trip loss', 'Revenue', 'Trailer breakdowns', 'Absent / on-rest drivers']

export function pick(obj: unknown, key: string): unknown {
  const dot = key.indexOf('.')
  if (dot < 0) return (obj as Record<string, unknown> | null)?.[key]
  // drivers.<vendor> / energy.<hub>: the vendor name may itself contain dots
  const inner = (obj as Record<string, unknown> | null)?.[key.slice(0, dot)]
  return inner == null ? undefined : (inner as Record<string, unknown>)[key.slice(dot + 1)]
}

export function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

const nf0 = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 })
const nf1 = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 1 })
const nf2 = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export const fmt0 = (v: number) => nf0.format(v)
export const fmt1 = (v: number) => nf1.format(v)

export function fmtDur(sec: number, withDays: boolean): string {
  const s = Math.max(0, Math.round(sec))
  const d = Math.floor(s / 86400)
  const h = Math.floor((s % 86400) / 3600)
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0')
  if (!withDays) return `${h + d * 24}:${m}`
  return d > 0 ? `${d}d ${String(h).padStart(2, '0')}:${m}` : `${h}:${m}`
}

export function fmtCell(v: unknown, kind?: Kind): string {
  const n = num(v)
  if (n == null) return v == null || v === '' ? '' : String(v)
  switch (kind) {
    case 'int': case 'km': case 'kwh': return nf0.format(n)
    case 'num': return nf1.format(n)
    case 'pct': return `${(n * 100).toFixed(1)}%`
    case 'ratio': return nf2.format(n)
    case 'dur': return fmtDur(n, true)
    case 'avgdur': return fmtDur(n, false)
    default: return nf1.format(n)
  }
}

/** Text colour for a ratio judged against 100%. */
export function pctTone(v: unknown): string {
  const n = num(v)
  if (n == null) return ''
  if (n >= 0.95) return 'text-emerald-700'
  if (n >= 0.8) return 'text-amber-700'
  return 'text-rose-700'
}

export function istToday(): string {
  return new Date(Date.now() + 5.5 * 3600_000).toISOString().slice(0, 10)
}

export function shiftDay(iso: string, by: number): string {
  return new Date(new Date(`${iso}T00:00:00Z`).getTime() + by * 86400_000).toISOString().slice(0, 10)
}

export function dayHead(iso: string): { d: string; w: string; long: string } {
  const dt = new Date(`${iso}T00:00:00Z`)
  const o = { timeZone: 'UTC' } as const
  return {
    d: dt.toLocaleDateString('en-GB', { ...o, day: '2-digit', month: 'short' }),
    w: dt.toLocaleDateString('en-GB', { ...o, weekday: 'short' }),
    long: dt.toLocaleDateString('en-GB', { ...o, weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
  }
}
